import React, { useState, useEffect, useMemo } from 'react';
import { Key, ArrowLeft, Shield, AlertTriangle, Lock, Server, Bot, Settings, X, Check, Loader, Tag, Layers, Plus, Trash2, Edit } from 'lucide-react';

import type { CredentialData } from './CredentialModal';
import { CustomDropdown } from '../../../components/CustomDropdown ';
import baseapi from '../../../services/baseapi';
import { showError, showSuccess } from '../../../utils/toast';
import MultiSelectApiDropdownWithAll from '../../../components/MultiSelectApiDropdownWithAll';
import MultiSelectApiDropdown from '../../../components/MultiSelectApiDropdown';

export interface DiscoveryDefaultItem {
    id: string;
    credential_type: 'snmp' | 'camera' | 'firewall' | 'access_point' | 'nvr' | 'vmware';
    credential_data: Record<string, any>;
}

interface CredentialFormProps {
    agent: {
        client_id: string;
        id: string;
    };
    organizationId: string;
    credential: CredentialData | null;
    isEdit: boolean;
    onSuccess: () => void;
    onBack: () => void;
}

const CredentialForm: React.FC<CredentialFormProps> = ({
    agent,
    organizationId,
    credential,
    isEdit,
    onSuccess,
    onBack
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [validationErrorMsg, setValidationErrorMsg] = useState<string | null>(null);

    const [formData, setFormData] = useState<CredentialData>({
        credential_name: '',
        credential_type: 'snmp',
        enabled: true,
        is_active: true,
        snmp_type: 'v1',
        community_string: '',
        snmp_username: '',
        auth_password: '',
        privacy_password: '',
        firewall_type: 'cisco',
        vmware_type: 'normal',
        cidrs: [],
        host: '',
        port: 443,
        log_monitor_enable: false,
        syslog_port: 443,
        channel: ''
    });

    const [ipAddresses, setIpAddresses] = useState<string[]>([]);
    const [selectedAsset, setSelectedAsset] = useState<any>(null);

    // New state for multi-select asset search, asset type filter, and tag filters
    const [selectedAssets, setSelectedAssets] = useState<any[]>([]);
    const [selectedAssetType, setSelectedAssetType] = useState<string>('all');
    const [selectedTags, setSelectedTags] = useState<any[]>([]);

    // State for discovery default fallback credentials
    const [discoveryDefaults, setDiscoveryDefaults] = useState<DiscoveryDefaultItem[]>([]);
    const [showAddDefaultModal, setShowAddDefaultModal] = useState<boolean>(false);
    const [editingDefaultId, setEditingDefaultId] = useState<string | null>(null);
    const [newDefaultType, setNewDefaultType] = useState<'snmp' | 'camera' | 'firewall' | 'access_point' | 'nvr' | 'vmware'>('camera');
    const [newDefaultData, setNewDefaultData] = useState<Record<string, any>>({});

    // Dropdown options for Asset Types (derived from AssetsTable)
    const assetTypeOptions = [
        { label: 'All Asset Types', value: 'all' },
        { label: 'EndPoint Servers', value: 'local_asset' },
        { label: 'Switches', value: 'switch' },
        { label: 'Cameras', value: 'camera' },
        { label: 'Network Devices', value: 'network_device' },
        { label: 'Unmanaged Switches', value: 'unmanaged_switch' },
        { label: 'Workstations', value: 'workstation' },
        { label: 'Access Points', value: 'access_point' },
        { label: 'NVR', value: 'nvr' },
        { label: 'Printers', value: 'printer' },
        { label: 'Firewalls', value: 'firewall' },
        { label: 'Routers', value: 'router' },
        { label: 'Storage Devices', value: 'storage' },
        { label: 'Mobile', value: 'mobile' },
        { label: 'iLO', value: 'ilo' },
        { label: 'Other', value: 'unknown' }
    ];

    // Build dynamic query for asset fetch dropdown
    const assetQueries = useMemo(() => {
        const queries: Record<string, any> = {
            client_id: Number(agent.client_id)
        };

        if (selectedAssetType && selectedAssetType !== 'all') {
            queries.asset_type = selectedAssetType;
        } else {
            queries.asset_type = { $notin: ['isp', 'external_asset'] };
        }

        if (selectedTags && selectedTags.length > 0) {
            const tagIds = selectedTags
                .map(t => (t.id != null ? Number(t.id) : null))
                .filter((id): id is number => id !== null && !isNaN(id));
            if (tagIds.length > 0) {
                queries.tag_ids = { $contains: tagIds };
            }
        }

        return queries;
    }, [agent.client_id, selectedAssetType, selectedTags]);

    // dropdowns for credential type
    const credentialTypeOptions = [
        { label: 'SNMP', value: 'snmp' },
        { label: 'Firewall', value: 'firewall' },
        // { label: 'VMware', value: 'vmware' },
        { label: 'Camera', value: 'camera' },
        { label: 'Discovery', value: 'discovery' },
        { label: 'Default', value: 'default' },
        { label: 'iLO', value: 'ilo' },
        { label: 'NVR', value: 'nvr' },
        { label: 'Access Point', value: 'access_point' }
    ];
    const snmpTypeOptions = [
        { label: 'SNMP v1', value: 'v1' },
        { label: 'SNMP v2', value: 'v2c' },
        { label: 'SNMP v3', value: 'v3' }
    ];
    const firewallTypeOptions = [
        { label: 'Cisco', value: 'cisco' },
        { label: 'FortiGate', value: 'fortigate' }
    ];
    const vmwareTypeOptions = [
        { label: 'Normal', value: 'normal' },
        { label: 'vCenter', value: 'vcenter' }
    ];

    useEffect(() => {
        if (isEdit && credential) {
            let parsedData: any = {};
            if (credential.credential_data) {
                try {
                    parsedData = typeof credential.credential_data === 'string'
                        ? JSON.parse(credential.credential_data)
                        : credential.credential_data;
                } catch (e) {
                    console.error('Failed to parse credential_data:', e);
                }
            }

            setFormData({
                ...credential,
                // Map fields from parsedData to formData
                snmp_type: parsedData.snmp_version || (credential as any).snmp_type,
                community_string: parsedData.community || (credential as any).community_string,
                snmp_username: parsedData.snmp_username || (credential as any).snmp_username,
                auth_protocol: parsedData.auth_protocol || (credential as any).auth_protocol,
                auth_password: parsedData.auth_password || (credential as any).auth_password,
                privacy_protocol: parsedData.priv_protocol || (credential as any).privacy_protocol,
                privacy_password: parsedData.priv_password || (credential as any).privacy_password,

                firewall_type: parsedData.firewall_type || (credential as any).firewall_type,
                hostname: parsedData.hostname || (credential as any).hostname,
                ssh_port: parsedData.ssh_port || (credential as any).ssh_port,
                https_port: parsedData.https_port || (credential as any).https_port,
                username: parsedData.username || (credential as any).username,
                password: parsedData.password || (credential as any).password,
                api_key: parsedData.api_key || (credential as any).api_key,
                log_monitor_enable: parsedData.log_monitor_enable || (credential as any).log_monitor_enable || false,
                syslog_port: parsedData.syslog_port || (credential as any).syslog_port || 514,

                vmware_type: parsedData.vmware_type || (credential as any).vmware_type,
                port: parsedData.port || (credential as any).port || parsedData.https_port || 443,
                private_key: parsedData.private_key || (credential as any).private_key,
                vcenter_host: parsedData.vcenter_host || (credential as any).vcenter_host,
                make: parsedData.make || (credential as any).make || '',
                channel: parsedData.channel || (credential as any).channel || '',
                cidrs: parsedData.cidrs || (credential as any).cidrs || [],
                host: parsedData.host || (credential as any).host || '',
                is_active: credential.is_active ?? true
            });

            // Set asset_type filter if available
            if ((credential as any).asset_type) {
                setSelectedAssetType((credential as any).asset_type);
            } else {
                setSelectedAssetType('all');
            }

            // Set selected tags (resolved_tags or fetch via tag_ids)
            if ((credential as any).resolved_tags && Array.isArray((credential as any).resolved_tags) && (credential as any).resolved_tags.length > 0) {
                setSelectedTags((credential as any).resolved_tags);
            } else if ((credential as any).tag_ids && Array.isArray((credential as any).tag_ids) && (credential as any).tag_ids.length > 0) {
                const fetchTags = async () => {
                    try {
                        const numericTagIds = (credential as any).tag_ids.map((id: any) => Number(id)).filter((id: number) => !isNaN(id));
                        const response = await baseapi.get('/tags', {
                            params: {
                                query: JSON.stringify({ id: { $in: numericTagIds } })
                            }
                        });
                        if (response.data?.status && Array.isArray(response.data?.data)) {
                            setSelectedTags(response.data.data);
                        } else {
                            setSelectedTags(numericTagIds.map((id: number) => ({ id, name: `Tag #${id}` })));
                        }
                    } catch (err) {
                        console.error('Failed to fetch tags for tag_ids:', err);
                        setSelectedTags((credential as any).tag_ids.map((id: any) => ({ id, name: `Tag #${id}` })));
                    }
                };
                fetchTags();
            } else {
                setSelectedTags([]);
            }

            // Parse discovery default credentials if available
            let parsedDefaults = parsedData.defaults;
            if (!parsedDefaults && (credential as any)?.credential_data) {
                try {
                    const cd = typeof (credential as any).credential_data === 'string'
                        ? JSON.parse((credential as any).credential_data)
                        : (credential as any).credential_data;
                    parsedDefaults = cd?.defaults;
                } catch (e) { }
            }

            if (parsedDefaults && typeof parsedDefaults === 'object') {
                const list: DiscoveryDefaultItem[] = [];
                Object.entries(parsedDefaults).forEach(([_, items]: [string, any]) => {
                    if (Array.isArray(items)) {
                        items.forEach((item: any) => {
                            if (item.credential_type) {
                                list.push({
                                    id: Math.random().toString(36).substr(2, 9),
                                    credential_type: item.credential_type,
                                    credential_data: item.credential_data || {}
                                });
                            }
                        });
                    }
                });
                setDiscoveryDefaults(list);
            } else {
                setDiscoveryDefaults([]);
            }

            // Set IP addresses if directly present
            if ((credential as any).ip_addresses && Array.isArray((credential as any).ip_addresses)) {
                setIpAddresses((credential as any).ip_addresses);
            } else if (credential.asset_info?.ip_address) {
                setIpAddresses([credential.asset_info.ip_address]);
            } else {
                setIpAddresses([]);
            }

            // Parse asset_ids (can be a JSON string like "[1940891]", array, or '*')
            let parsedAssetIds: any[] = [];
            const rawAssetIds = (credential as any).asset_ids;
            const isAllAssets = rawAssetIds === '*' || rawAssetIds === '"*"' || (Array.isArray(rawAssetIds) && rawAssetIds.includes('*')) || (typeof rawAssetIds === 'string' && rawAssetIds.includes('*'));

            if (isAllAssets) {
                setSelectedAssets([{ id: '*', name: 'Select all' }]);
                setSelectedAsset({ id: '*', name: 'Select all' });
            } else if (typeof rawAssetIds === 'string') {
                try {
                    parsedAssetIds = JSON.parse(rawAssetIds);
                } catch (e) {
                    console.error('Failed to parse asset_ids string:', e);
                }
            } else if (Array.isArray(rawAssetIds)) {
                parsedAssetIds = rawAssetIds;
            }

            if (!isAllAssets && parsedAssetIds.length > 0) {
                const fetchAssetDetails = async () => {
                    try {
                        const numericIds = parsedAssetIds.map((id: any) => Number(id)).filter((id: number) => !isNaN(id));
                        const response = await baseapi.get('/assets', {
                            params: {
                                query: JSON.stringify({ id: { $in: numericIds } }),
                                limit: numericIds.length
                            }
                        });
                        if (response.data?.status && Array.isArray(response.data?.data)) {
                            const fetchedAssets = response.data.data;
                            setSelectedAssets(fetchedAssets);
                            const fetchedIps = fetchedAssets.map((a: any) => a.ip_address).filter(Boolean);
                            if (fetchedIps.length > 0) {
                                setIpAddresses(fetchedIps);
                            }
                            if (fetchedAssets.length > 0) {
                                setSelectedAsset(fetchedAssets[0]);
                            }
                        }
                    } catch (err) {
                        console.error('Failed to fetch assets for asset_ids:', err);
                    }
                };
                fetchAssetDetails();
            } else if (!isAllAssets && credential.asset_info && credential.asset_info.id) {
                setSelectedAsset(credential.asset_info);
                setSelectedAssets([credential.asset_info]);
            } else if (!isAllAssets && credential.asset_id) {
                const fetchAsset = async () => {
                    try {
                        const response = await baseapi.get(`/assets/${credential.asset_id}`);
                        if (response.data?.status && response.data?.data) {
                            setSelectedAsset(response.data.data);
                            setSelectedAssets([response.data.data]);
                            if (response.data.data.ip_address) {
                                setIpAddresses([response.data.data.ip_address]);
                            }
                        }
                    } catch (e) {
                        console.error('Failed to fetch asset details:', e);
                    }
                };
                fetchAsset();
            } else if (!isAllAssets) {
                setSelectedAssets([]);
                setSelectedAsset(null);
            }
        }
    }, [isEdit, credential]);

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.credential_name.trim()) {
            newErrors.credential_name = 'Credential name is required';
        }

        // Must have at least one IP or Select All - Not required for discovery or default
        const isSelectAllAssets = selectedAssets.some(a => a.id === '*');
        if (formData.credential_type !== 'discovery' && formData.credential_type !== 'default' && ipAddresses.length < 1 && !isSelectAllAssets) {
            newErrors.asset_id = 'Please select at least one asset to add IP';
        }

        // SNMP/Access Point Validation
        if (formData.credential_type === 'snmp' || formData.credential_type === 'access_point') {
            if (formData.snmp_type !== 'v3') {
                if (!formData.community_string?.trim()) {
                    newErrors.community_string = 'Community string is required';
                }
            } else {
                if (!formData.snmp_username?.trim()) {
                    newErrors.snmp_username = 'SNMP Username is required';
                }
                if (!formData.auth_protocol?.trim()) {
                    newErrors.auth_protocol = 'Auth protocol is required for SNMP v3';
                }
                if (!formData.auth_password?.trim()) {
                    newErrors.auth_password = 'Auth password is required for SNMP v3';
                }
                if (!formData.privacy_protocol?.trim()) {
                    newErrors.privacy_protocol = 'Privacy protocol is required for SNMP v3';
                }
                if (!formData.privacy_password?.trim()) {
                    newErrors.privacy_password = 'Privacy password is required for SNMP v3';
                }
            }
        }

        // NVR Validation (Same as SNMP v1/v2)
        if (formData.credential_type === 'nvr') {
            if (!formData.community_string?.trim()) {
                newErrors.community_string = 'Community string is required';
            }
        }

        // Firewall
        if (formData.credential_type === 'firewall') {
            // Hostname is auto-set from selected asset in handleSubmit
            if (formData.firewall_type === 'cisco') {
                if (!formData.ssh_port) {
                    newErrors.ssh_port = 'SSH port is required';
                }
                if (!formData.username?.trim()) {
                    newErrors.username = 'Username is required';
                }
                if (!formData.password?.trim()) {
                    newErrors.password = 'Password is required';
                }
            } else if (formData.firewall_type === 'fortigate') {
                if (!formData.https_port) {
                    newErrors.https_port = 'HTTPS port is required';
                }
                if (!formData.api_key?.trim()) {
                    newErrors.api_key = 'API key is required';
                }
                if (formData.log_monitor_enable && !formData.syslog_port) {
                    newErrors.syslog_port = 'Syslog port is required';
                }
            }
        }

        // VMware
        if (formData.credential_type === 'vmware') {
            if (!formData.port) {
                newErrors.port = 'Port is required';
            }
            if (!formData.username?.trim()) {
                newErrors.username = 'Username is required';
            }
            if (!formData.password?.trim()) {
                newErrors.password = 'Password is required';
            }
            if (!formData.private_key?.trim()) {
                newErrors.private_key = 'Private key is required';
            }
            if (formData.vmware_type === 'vcenter' && !formData.vcenter_host?.trim()) {
                newErrors.vcenter_host = 'vCenter host is required';
            }
        }

        // Camera
        if (formData.credential_type === 'camera') {
            if (!formData.username?.trim()) {
                newErrors.username = 'Username is required';
            }
            if (!formData.password?.trim()) {
                newErrors.password = 'Password is required';
            }
        }

        // Discovery Validation
        if (formData.credential_type === 'discovery') {
            const hasCidrs = formData.cidrs && formData.cidrs.length > 0;
            if (!hasCidrs) {
                newErrors.cidrs = 'Please enter at least one CIDR subnet';
            } else {
                const invalidCidrs = formData.cidrs!.filter(cidr => {
                    const parts = cidr.split('/');
                    if (parts.length !== 2) return true;
                    const [ip, prefixStr] = parts;
                    const prefix = parseInt(prefixStr, 10);
                    if (isNaN(prefix) || prefix < 16 || prefix > 32) return true;
                    const ipv4Regex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
                    return !ipv4Regex.test(ip);
                });
                if (invalidCidrs.length > 0) {
                    newErrors.cidrs = 'Invalid CIDR format or prefix not in /16 to /32 range';
                }
            }
        }

        // Default Credentials Validation
        if (formData.credential_type === 'default') {
            if (discoveryDefaults.length < 1) {
                newErrors.defaults = 'Please add at least one default fallback credential';
            }
        }

        // iLO Validation
        if (formData.credential_type === 'ilo') {
            if (!formData.host?.trim()) {
                newErrors.host = 'Host is required';
            }
            if (!formData.username?.trim()) {
                newErrors.username = 'Username is required';
            }
            if (!formData.password?.trim()) {
                newErrors.password = 'Password is required';
            }
        }

        setErrors(newErrors);
        const errorList = Object.values(newErrors);
        if (errorList.length > 0) {
            setValidationErrorMsg(`Validation failed: ${errorList[0]}`);
        } else {
            setValidationErrorMsg(null);
        }
        return errorList.length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setValidationErrorMsg(null);

        if (!validateForm()) return;

        setIsSubmitting(true);

        const tagIds = selectedTags
            .map(t => (t.id != null ? Number(t.id) : null))
            .filter((id): id is number => id !== null && !isNaN(id));

        const isSelectAllAssets = selectedAssets.some(a => a.id === '*');
        const assetIds = isSelectAllAssets
            ? ['*']
            : selectedAssets
                .map(a => a.id)
                .filter(id => id != null);

        // 1. Create a base object with common fields
        const basePayload = {
            client_id: Number(agent.client_id),
            agent_id: Number(agent.id),
            organization_id: organizationId,
            credential_name: formData.credential_name,
            credential_type: formData.credential_type,
            enabled: formData.enabled,
            is_active: formData.is_active,
            ip_addresses: isSelectAllAssets ? [] : ipAddresses,
            asset_ids: assetIds,
            asset_type: selectedAssetType,
            tag_ids: tagIds
        };

        // 2. Create a type-specific payload
        let typeSpecificData = {};

        switch (formData.credential_type) {
            case 'snmp':
            case 'nvr':
            case 'access_point':
                typeSpecificData = {
                    snmp_version: formData.credential_type === 'nvr' ? 'v2c' : formData.snmp_type,
                    // Conditional data based on version
                    ...((formData.credential_type === 'nvr' || formData.snmp_type !== 'v3') ? {
                        community: formData.community_string,
                    } : {
                        snmp_username: formData.snmp_username,
                        auth_protocol: formData.auth_protocol,
                        auth_password: formData.auth_password,
                        priv_protocol: formData.privacy_protocol,
                        priv_password: formData.privacy_password
                    }),
                    make: formData.make
                };
                break;

            case 'firewall':
                typeSpecificData = {
                    firewall_type: formData.firewall_type,
                    hostname: selectedAsset ? (selectedAsset.name || selectedAsset.ip_address) : formData.hostname,
                };

                if (formData.firewall_type === 'cisco') {
                    typeSpecificData = {
                        ...typeSpecificData,
                        ssh_port: formData.ssh_port,
                        username: formData.username,
                        password: formData.password
                    };
                } else if (formData.firewall_type === 'fortigate') {
                    typeSpecificData = {
                        ...typeSpecificData,
                        https_port: formData.https_port,
                        api_key: formData.api_key,
                        log_monitor_enable: formData.log_monitor_enable || false,
                        ...(formData.log_monitor_enable && {
                            syslog_port: formData.syslog_port || 514
                        })
                    };
                }
                break;

            case 'vmware':
                typeSpecificData = {
                    vmware_type: formData.vmware_type,
                    port: formData.port,
                    username: formData.username,
                    password: formData.password,
                    private_key: formData.private_key,
                    // Only include vcenter_host if vcenter is selected
                    ...(formData.vmware_type === 'vcenter' && {
                        vcenter_host: formData.vcenter_host
                    })
                };
                break;

            case 'camera':
                typeSpecificData = {
                    username: formData.username,
                    password: formData.password,
                    make: formData.make,
                    channel: formData.channel !== '' ? Number(formData.channel) : null
                };
                break;

            case 'discovery':
                typeSpecificData = {
                    cidrs: formData.cidrs || []
                };
                break;

            case 'default':
                const defaultsObj: Record<string, any[]> = {};
                discoveryDefaults.forEach((item) => {
                    const key = item.credential_type === 'snmp'
                        ? 'switch_credentials'
                        : `${item.credential_type}_credentials`;
                    if (!defaultsObj[key]) {
                        defaultsObj[key] = [];
                    }
                    defaultsObj[key].push({
                        credential_type: item.credential_type,
                        credential_data: item.credential_data
                    });
                });

                typeSpecificData = {
                    defaults: defaultsObj
                };
                break;

            case 'ilo':
                typeSpecificData = {
                    host: formData.host,
                    username: formData.username,
                    password: formData.password,
                    port: formData.port || 443
                };
                break;
        }

        // 3. Merge them into the final payload
        const finalPayload = {
            ...basePayload,
            credential_data: {
                ...typeSpecificData
            }
        };


        try {
            let credentialId: string | undefined;
            if (isEdit && credential?.id) {
                credentialId = credential.id;
            }

            const endpoint = isEdit ? `/credentials/${credentialId}` : '/credentials';
            const method = isEdit ? 'put' : 'post';

            // Use finalPayload instead of params
            const response = await baseapi[method](endpoint, finalPayload);

            if (response.data.status) {
                showSuccess(isEdit ? 'Credential updated successfully' : 'Credential configured successfully');
                onSuccess();
            } else {
                showError(response.data.message);
            }
        } catch (error) {
            console.error('Submit failed:', error);
            showError('Failed to save credential configuration');
        } finally {
            setIsSubmitting(false);
        }
    };


    // IP Entry Handlers
    // const handleAddIpAddress = () => {
    //     if (!newIpAddress.trim()) return;
    //     if (!isValidIPv4(newIpAddress.trim())) {
    //         setIpErrors('Invalid IP address format');
    //         return;
    //     }
    //     if (ipAddresses.includes(newIpAddress.trim())) {
    //         setIpErrors('Duplicate IP address');
    //         return;
    //     }
    //     setIpAddresses([...ipAddresses, newIpAddress.trim()]);
    //     setNewIpAddress('');
    //     setIpErrors(null);
    // };
    // const handleRemoveIpAddress = (index: number) => {
    //     setIpAddresses(ipAddresses.filter((_, idx) => idx !== index));
    // };

    const handleSaveDefaultCredential = () => {
        let credData: Record<string, any> = { ...newDefaultData };

        if (newDefaultType === 'snmp' || newDefaultType === 'access_point' || newDefaultType === 'nvr') {
            if (!credData.snmp_version) {
                credData.snmp_version = 'v2c';
            }
        } else if (newDefaultType === 'firewall') {
            if (!credData.firewall_type) {
                credData.firewall_type = 'fortigate';
            }
        } else if (newDefaultType === 'vmware') {
            if (!credData.vmware_type) {
                credData.vmware_type = 'normal';
            }
        }

        if (editingDefaultId) {
            setDiscoveryDefaults(prev => prev.map(item =>
                item.id === editingDefaultId
                    ? { ...item, credential_type: newDefaultType, credential_data: credData }
                    : item
            ));
        } else {
            const item: DiscoveryDefaultItem = {
                id: Math.random().toString(36).substr(2, 9),
                credential_type: newDefaultType,
                credential_data: credData
            };
            setDiscoveryDefaults(prev => [...prev, item]);
        }

        setEditingDefaultId(null);
        setNewDefaultData({});
        setShowAddDefaultModal(false);
    };

    const handleEditDefaultCredential = (item: DiscoveryDefaultItem) => {
        setEditingDefaultId(item.id);
        setNewDefaultType(item.credential_type);
        setNewDefaultData({ ...item.credential_data });
        setShowAddDefaultModal(true);
    };

    const renderNewDefaultFields = () => {
        const inputBaseClass = `w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700/50 rounded px-2 py-1.5 text-[11px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:border-orange-500/50 transition-all duration-200 font-mono`;
        const labelBaseClass = `flex items-center text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-[0.1em] mb-1.5`;

        switch (newDefaultType) {
            case 'snmp':
            case 'access_point':
            case 'nvr':
                const snmpVersion = newDefaultData.snmp_version || 'v2c';
                return (
                    <>
                        <div className="md:col-span-2">
                            <label className={labelBaseClass}>SNMP Version</label>
                            <CustomDropdown
                                data={[
                                    { label: 'SNMP v1', value: 'v1' },
                                    { label: 'SNMP v2c', value: 'v2c' },
                                    { label: 'SNMP v3', value: 'v3' }
                                ]}
                                placeholder="Select Version"
                                selectedValue={snmpVersion}
                                onSelect={(v) => setNewDefaultData(prev => ({ ...prev, snmp_version: v }))}
                                width="w-full"
                                buttonHeight={8}
                            />
                        </div>
                        {snmpVersion === 'v3' ? (
                            <>
                                <div>
                                    <label className={labelBaseClass}>SNMP Username</label>
                                    <input
                                        type="text"
                                        value={newDefaultData.snmp_username || ''}
                                        onChange={(e) => setNewDefaultData(prev => ({ ...prev, snmp_username: e.target.value }))}
                                        className={inputBaseClass}
                                        placeholder="Username"
                                    />
                                </div>
                                <div>
                                    <label className={labelBaseClass}>Auth Protocol</label>
                                    <input
                                        type="text"
                                        value={newDefaultData.auth_protocol || 'SHA'}
                                        onChange={(e) => setNewDefaultData(prev => ({ ...prev, auth_protocol: e.target.value }))}
                                        className={inputBaseClass}
                                        placeholder="SHA"
                                    />
                                </div>
                                <div>
                                    <label className={labelBaseClass}>Auth Password</label>
                                    <input
                                        type="password"
                                        value={newDefaultData.auth_password || ''}
                                        onChange={(e) => setNewDefaultData(prev => ({ ...prev, auth_password: e.target.value }))}
                                        className={inputBaseClass}
                                        placeholder="Auth Password"
                                    />
                                </div>
                                <div>
                                    <label className={labelBaseClass}>Priv Protocol</label>
                                    <input
                                        type="text"
                                        value={newDefaultData.priv_protocol || 'AES'}
                                        onChange={(e) => setNewDefaultData(prev => ({ ...prev, priv_protocol: e.target.value }))}
                                        className={inputBaseClass}
                                        placeholder="AES"
                                    />
                                </div>
                                <div>
                                    <label className={labelBaseClass}>Priv Password</label>
                                    <input
                                        type="password"
                                        value={newDefaultData.priv_password || ''}
                                        onChange={(e) => setNewDefaultData(prev => ({ ...prev, priv_password: e.target.value }))}
                                        className={inputBaseClass}
                                        placeholder="Priv Password"
                                    />
                                </div>
                            </>
                        ) : (
                            <div className="md:col-span-2">
                                <label className={labelBaseClass}>Community String</label>
                                <input
                                    type="text"
                                    value={newDefaultData.community || ''}
                                    onChange={(e) => setNewDefaultData(prev => ({ ...prev, community: e.target.value }))}
                                    className={inputBaseClass}
                                    placeholder="public"
                                />
                            </div>
                        )}
                        {newDefaultType === 'nvr' && (
                            <>
                                <div>
                                    <label className={labelBaseClass}>User (Optional)</label>
                                    <input
                                        type="text"
                                        value={newDefaultData.username || ''}
                                        onChange={(e) => setNewDefaultData(prev => ({ ...prev, username: e.target.value }))}
                                        className={inputBaseClass}
                                        placeholder="admin"
                                    />
                                </div>
                                <div>
                                    <label className={labelBaseClass}>Password (Optional)</label>
                                    <input
                                        type="password"
                                        value={newDefaultData.password || ''}
                                        onChange={(e) => setNewDefaultData(prev => ({ ...prev, password: e.target.value }))}
                                        className={inputBaseClass}
                                        placeholder="Password"
                                    />
                                </div>
                            </>
                        )}
                        <div className="md:col-span-2">
                            <label className={labelBaseClass}>Make (Optional)</label>
                            <input
                                type="text"
                                value={newDefaultData.make || ''}
                                onChange={(e) => setNewDefaultData(prev => ({ ...prev, make: e.target.value }))}
                                className={inputBaseClass}
                                placeholder="e.g. Cisco, HP, Aruba"
                            />
                        </div>
                    </>
                );

            case 'camera':
                return (
                    <>
                        <div>
                            <label className={labelBaseClass}>Username</label>
                            <input
                                type="text"
                                value={newDefaultData.username || ''}
                                onChange={(e) => setNewDefaultData(prev => ({ ...prev, username: e.target.value }))}
                                className={inputBaseClass}
                                placeholder="admin"
                            />
                        </div>
                        <div>
                            <label className={labelBaseClass}>Password</label>
                            <input
                                type="password"
                                value={newDefaultData.password || ''}
                                onChange={(e) => setNewDefaultData(prev => ({ ...prev, password: e.target.value }))}
                                className={inputBaseClass}
                                placeholder="Password"
                            />
                        </div>
                        <div>
                            <label className={labelBaseClass}>Make (Optional)</label>
                            <input
                                type="text"
                                value={newDefaultData.make || ''}
                                onChange={(e) => setNewDefaultData(prev => ({ ...prev, make: e.target.value }))}
                                className={inputBaseClass}
                                placeholder="Hikvision, Dahua"
                            />
                        </div>
                        <div>
                            <label className={labelBaseClass}>Channel (Optional)</label>
                            <input
                                type="number"
                                value={newDefaultData.channel ?? ''}
                                onChange={(e) => setNewDefaultData(prev => ({ ...prev, channel: e.target.value }))}
                                className={inputBaseClass}
                                placeholder="1"
                            />
                        </div>
                    </>
                );

            case 'firewall':
                const fwType = newDefaultData.firewall_type || 'fortigate';
                return (
                    <>
                        <div className="md:col-span-2">
                            <label className={labelBaseClass}>Firewall Brand</label>
                            <CustomDropdown
                                data={[
                                    { label: 'FortiGate', value: 'fortigate' },
                                    { label: 'Cisco', value: 'cisco' },
                                    { label: 'Palo Alto', value: 'paloalto' }
                                ]}
                                placeholder="Select Firewall Brand"
                                selectedValue={fwType}
                                onSelect={(v) => setNewDefaultData(prev => ({ ...prev, firewall_type: v }))}
                                width="w-full"
                                buttonHeight={8}
                            />
                        </div>
                        {fwType === 'fortigate' ? (
                            <>
                                <div>
                                    <label className={labelBaseClass}>Hostname (Optional)</label>
                                    <input
                                        type="text"
                                        value={newDefaultData.hostname || ''}
                                        onChange={(e) => setNewDefaultData(prev => ({ ...prev, hostname: e.target.value }))}
                                        className={inputBaseClass}
                                        placeholder="_gateway"
                                    />
                                </div>
                                <div>
                                    <label className={labelBaseClass}>HTTPS Port (Optional)</label>
                                    <input
                                        type="number"
                                        value={newDefaultData.https_port ?? ''}
                                        onChange={(e) => setNewDefaultData(prev => ({ ...prev, https_port: e.target.value ? Number(e.target.value) : undefined }))}
                                        className={inputBaseClass}
                                        placeholder="443"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className={labelBaseClass}>API Key</label>
                                    <input
                                        type="password"
                                        value={newDefaultData.api_key || ''}
                                        onChange={(e) => setNewDefaultData(prev => ({ ...prev, api_key: e.target.value }))}
                                        className={inputBaseClass}
                                        placeholder="API Key"
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <div>
                                    <label className={labelBaseClass}>Username</label>
                                    <input
                                        type="text"
                                        value={newDefaultData.username || ''}
                                        onChange={(e) => setNewDefaultData(prev => ({ ...prev, username: e.target.value }))}
                                        className={inputBaseClass}
                                        placeholder="admin"
                                    />
                                </div>
                                <div>
                                    <label className={labelBaseClass}>Password</label>
                                    <input
                                        type="password"
                                        value={newDefaultData.password || ''}
                                        onChange={(e) => setNewDefaultData(prev => ({ ...prev, password: e.target.value }))}
                                        className={inputBaseClass}
                                        placeholder="Password"
                                    />
                                </div>
                            </>
                        )}
                    </>
                );

            case 'vmware':
                const vmType = newDefaultData.vmware_type || 'normal';
                return (
                    <>
                        <div className="md:col-span-2">
                            <label className={labelBaseClass}>VMware Mode</label>
                            <CustomDropdown
                                data={[
                                    { label: 'Normal ESXi', value: 'normal' },
                                    { label: 'vCenter', value: 'vcenter' }
                                ]}
                                placeholder="Select Mode"
                                selectedValue={vmType}
                                onSelect={(v) => setNewDefaultData(prev => ({ ...prev, vmware_type: v }))}
                                width="w-full"
                                buttonHeight={8}
                            />
                        </div>
                        {vmType === 'vcenter' && (
                            <div className="md:col-span-2">
                                <label className={labelBaseClass}>vCenter Host</label>
                                <input
                                    type="text"
                                    value={newDefaultData.vcenter_host || ''}
                                    onChange={(e) => setNewDefaultData(prev => ({ ...prev, vcenter_host: e.target.value }))}
                                    className={inputBaseClass}
                                    placeholder="vcenter.local"
                                />
                            </div>
                        )}
                        <div>
                            <label className={labelBaseClass}>Username</label>
                            <input
                                type="text"
                                value={newDefaultData.username || ''}
                                onChange={(e) => setNewDefaultData(prev => ({ ...prev, username: e.target.value }))}
                                className={inputBaseClass}
                                placeholder="root"
                            />
                        </div>
                        <div>
                            <label className={labelBaseClass}>Password</label>
                            <input
                                type="password"
                                value={newDefaultData.password || ''}
                                onChange={(e) => setNewDefaultData(prev => ({ ...prev, password: e.target.value }))}
                                className={inputBaseClass}
                                placeholder="Password"
                            />
                        </div>
                    </>
                );

            default:
                return null;
        }
    };

    // Credential fields rendering
    const renderCredentialFields = () => {
        const inputBaseClass = `w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700/50 rounded px-2 py-1.5 text-[11px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:border-orange-500/50 transition-all duration-200 font-mono`;
        const labelBaseClass = `flex items-center text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-[0.1em] mb-1.5`;
        const errorClass = `mt-1 text-[9px] text-red-500 dark:text-red-400 font-bold uppercase tracking-tight flex items-center`;

        switch (formData.credential_type) {
            case 'snmp':
            case 'access_point':
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
                        <div className="md:col-span-2">
                            <label className={labelBaseClass}>
                                <Shield className="w-3 h-3 mr-1.5 text-blue-400" />
                                SNMP Version <span className="text-red-500 ml-0.5">*</span>
                            </label>
                            <CustomDropdown
                                data={snmpTypeOptions}
                                placeholder="Select Version"
                                selectedValue={formData.snmp_type || 'v1'}
                                onSelect={(value) => {
                                    setFormData(prev => ({
                                        ...prev,
                                        snmp_type: value as 'v1' | 'v2c' | 'v3',
                                        community_string: '',
                                        snmp_username: '',
                                        auth_protocol: '',
                                        auth_password: '',
                                        privacy_protocol: '',
                                        privacy_password: ''
                                    }));
                                    setErrors({});
                                }}
                                width="w-full"
                                buttonHeight={8}
                            />
                        </div>
                        {formData.snmp_type !== 'v3' ? (
                            <div className="md:col-span-2">
                                <label className={labelBaseClass}>
                                    <Key className="w-3 h-3 mr-1.5 text-cyan-400" />
                                    Community String <span className="text-red-500 ml-0.5">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.community_string || ''}
                                    onChange={(e) => {
                                        setFormData(prev => ({ ...prev, community_string: e.target.value }));
                                        if (errors.community_string) {
                                            setErrors(prev => {
                                                const { community_string, ...rest } = prev;
                                                return rest;
                                            });
                                        }
                                    }}
                                    className={inputBaseClass}
                                    placeholder="Enter community string"
                                />
                                {errors.community_string && (
                                    <p className={errorClass}>
                                        <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                                        {errors.community_string}
                                    </p>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="md:col-span-2">
                                    <label className={labelBaseClass}>
                                        <Lock className="w-3 h-3 mr-1.5 text-cyan-400" />
                                        SNMP Username <span className="text-red-500 ml-0.5">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.snmp_username || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, snmp_username: e.target.value }))}
                                        className={inputBaseClass}
                                        placeholder="Username"
                                    />
                                    {errors.snmp_username && (
                                        <p className={errorClass}>
                                            <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                                            {errors.snmp_username}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className={labelBaseClass}>
                                        <Lock className="w-3 h-3 mr-1.5 text-green-400" />
                                        Auth Protocol
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.auth_protocol || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, auth_protocol: e.target.value }))}
                                        className={inputBaseClass}
                                        placeholder="MD5/SHA"
                                    />
                                </div>
                                <div>
                                    <label className={labelBaseClass}>
                                        <Lock className="w-3 h-3 mr-1.5 text-green-400" />
                                        Auth Password
                                    </label>
                                    <input
                                        type="password"
                                        value={formData.auth_password || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, auth_password: e.target.value }))}
                                        className={inputBaseClass}
                                        placeholder="Password"
                                    />
                                </div>
                                <div>
                                    <label className={labelBaseClass}>
                                        <Lock className="w-3 h-3 mr-1.5 text-purple-400" />
                                        Privacy Protocol
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.privacy_protocol || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, privacy_protocol: e.target.value }))}
                                        className={inputBaseClass}
                                        placeholder="DES/AES"
                                    />
                                </div>
                                <div>
                                    <label className={labelBaseClass}>
                                        <Lock className="w-3 h-3 mr-1.5 text-purple-400" />
                                        Privacy Password
                                    </label>
                                    <input
                                        type="password"
                                        value={formData.privacy_password || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, privacy_password: e.target.value }))}
                                        className={inputBaseClass}
                                        placeholder="Password"
                                    />
                                </div>
                            </>
                        )}
                        <div className="md:col-span-2">
                            <label className={labelBaseClass}>
                                <Bot className="w-3 h-3 mr-1.5 text-orange-400" />
                                Make (Optional)
                            </label>
                            <input
                                type="text"
                                value={formData.make || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, make: e.target.value }))}
                                className={inputBaseClass}
                                placeholder="e.g. Cisco, HP, Aruba"
                            />
                        </div>
                    </div>
                );
            case 'nvr':
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
                        <div className="md:col-span-2 text-xs">
                            <div className="bg-orange-500/5 border border-orange-500/20 rounded-lg p-3 mb-1">
                                <p className="text-[10px] text-orange-400 font-bold uppercase tracking-wider mb-1 flex items-center gap-2">
                                    <Shield className="w-3.5 h-3.5" />
                                    NVR SNMP Configuration
                                </p>
                                <p className="text-[9px] text-gray-600 dark:text-gray-400 leading-relaxed font-medium">
                                    Monitoring for NVR units is established via SNMP v2 protocol. Community string and Make are required for successful integration.
                                </p>
                            </div>
                        </div>
                        <div className="md:col-span-2">
                            <label className={labelBaseClass}>
                                <Shield className="w-3 h-3 mr-1.5 text-blue-400" />
                                SNMP Version <span className="text-red-500 ml-0.5">*</span>
                            </label>
                            <CustomDropdown
                                data={snmpTypeOptions}
                                placeholder="Select Version"
                                selectedValue={'v2c'}
                                onSelect={() => { }}
                                width="w-full"
                                buttonHeight={8}
                                disabled={true}
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className={labelBaseClass}>
                                <Key className="w-3 h-3 mr-1.5 text-cyan-400" />
                                Community String <span className="text-red-500 ml-0.5">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.community_string || ''}
                                onChange={(e) => {
                                    setFormData(prev => ({ ...prev, community_string: e.target.value }));
                                    if (errors.community_string) {
                                        setErrors(prev => {
                                            const { community_string, ...rest } = prev;
                                            return rest;
                                        });
                                    }
                                }}
                                className={inputBaseClass}
                                placeholder="Enter community string"
                            />
                            {errors.community_string && (
                                <p className={errorClass}>
                                    <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                                    {errors.community_string}
                                </p>
                            )}
                        </div>
                        <div className="md:col-span-2">
                            <label className={labelBaseClass}>
                                <Bot className="w-3 h-3 mr-1.5 text-orange-400" />
                                Make (Optional)
                            </label>
                            <input
                                type="text"
                                value={formData.make || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, make: e.target.value }))}
                                className={inputBaseClass}
                                placeholder="e.g. Hikvision, Dahua"
                            />
                        </div>
                    </div>
                );
            case 'firewall':
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
                        <div className="md:col-span-2">
                            <label className={labelBaseClass}>
                                <Shield className="w-3 h-3 mr-1.5 text-red-400" />
                                Firewall Vendor <span className="text-red-500 ml-0.5">*</span>
                            </label>
                            <CustomDropdown
                                data={firewallTypeOptions}
                                placeholder="Select Vendor"
                                selectedValue={formData.firewall_type || 'cisco'}
                                onSelect={(value) => {
                                    setFormData(prev => ({
                                        ...prev,
                                        firewall_type: value as 'cisco' | 'fortigate',
                                        ssh_port: undefined,
                                        https_port: undefined,
                                        username: '',
                                        password: '',
                                        api_key: '',
                                        log_monitor_enable: false,
                                        syslog_port: 514
                                    }));
                                    setErrors({});
                                }}
                                width="w-full"
                                buttonHeight={8}
                            />
                        </div>
                        {formData.firewall_type === 'cisco' ? (
                            <>
                                <div>
                                    <label className={labelBaseClass}>
                                        <Server className="w-3 h-3 mr-1.5 text-blue-400" />
                                        SSH Port
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.ssh_port || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, ssh_port: parseInt(e.target.value) || undefined }))}
                                        className={inputBaseClass}
                                        placeholder="22"
                                    />
                                </div>
                                <div>
                                    <label className={labelBaseClass}>
                                        <Key className="w-3 h-3 mr-1.5 text-green-400" />
                                        User
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.username || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                                        className={inputBaseClass}
                                        placeholder="User"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className={labelBaseClass}>
                                        <Lock className="w-3 h-3 mr-1.5 text-yellow-400" />
                                        Password
                                    </label>
                                    <input
                                        type="password"
                                        value={formData.password || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                                        className={inputBaseClass}
                                        placeholder="Password"
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <div>
                                    <label className={labelBaseClass}>
                                        <Server className="w-3 h-3 mr-1.5 text-blue-400" />
                                        HTTPS Port
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.https_port || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, https_port: parseInt(e.target.value) || undefined }))}
                                        className={inputBaseClass}
                                        placeholder="443"
                                    />
                                </div>
                                <div className="flex flex-col">
                                    <label className={labelBaseClass}>
                                        <Shield className="w-3 h-3 mr-1.5 text-orange-400" />
                                        Log Monitor
                                    </label>
                                    <div className="flex items-center h-[26px]">
                                        <button
                                            type="button"
                                            onClick={() => setFormData(prev => ({
                                                ...prev,
                                                log_monitor_enable: !prev.log_monitor_enable,
                                                syslog_port: !prev.log_monitor_enable ? (prev.syslog_port || 514) : prev.syslog_port
                                            }))}
                                            className="flex items-center gap-2 group transition-all focus:outline-none"
                                        >
                                            <div className={`
                                                relative w-8 h-4 rounded-full transition-all duration-300 ease-in-out border
                                                ${formData.log_monitor_enable
                                                    ? 'bg-emerald-600 border-emerald-500/30'
                                                    : 'bg-gray-300 dark:bg-gray-900 border-gray-400 dark:border-gray-700/50'}
                                            `}>
                                                <div className={`
                                                    absolute top-[2px] w-2.5 h-2.5 rounded-full bg-white transition-all duration-300 ease-in-out
                                                    ${formData.log_monitor_enable ? 'left-[16px]' : 'left-[2px]'}
                                                `} />
                                            </div>
                                            <span className={`text-[10px] font-bold uppercase tracking-wider transition-colors duration-200 ${formData.log_monitor_enable ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500 dark:text-gray-400'}`}>
                                                {formData.log_monitor_enable ? 'Enabled' : 'Disabled'}
                                            </span>
                                        </button>
                                    </div>
                                </div>
                                {formData.log_monitor_enable && (
                                    <div className="md:col-span-2">
                                        <label className={labelBaseClass}>
                                            <Server className="w-3 h-3 mr-1.5 text-blue-400" />
                                            System Log Port <span className="text-red-500 ml-0.5">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.syslog_port ?? ''}
                                            onChange={(e) => {
                                                setFormData(prev => ({ ...prev, syslog_port: parseInt(e.target.value) || undefined }));
                                                if (errors.syslog_port) {
                                                    setErrors(prev => {
                                                        const { syslog_port, ...rest } = prev;
                                                        return rest;
                                                    });
                                                }
                                            }}
                                            className={inputBaseClass}
                                            placeholder="514"
                                        />
                                        {errors.syslog_port && (
                                            <p className={errorClass}>
                                                <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                                                {errors.syslog_port}
                                            </p>
                                        )}
                                    </div>
                                )}
                                <div className="md:col-span-2">
                                    <label className={labelBaseClass}>
                                        <Key className="w-3 h-3 mr-1.5 text-purple-400" />
                                        API Key
                                    </label>
                                    <input
                                        type="password"
                                        value={formData.api_key || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, api_key: e.target.value }))}
                                        className={inputBaseClass}
                                        placeholder="FortiGate Key"
                                    />
                                </div>
                            </>
                        )}
                    </div>
                );
            case 'vmware':
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
                        <div className="md:col-span-2">
                            <label className={labelBaseClass}>
                                <Server className="w-3 h-3 mr-1.5 text-green-400" />
                                Type <span className="text-red-500 ml-0.5">*</span>
                            </label>
                            <CustomDropdown
                                data={vmwareTypeOptions}
                                placeholder="Select Type"
                                selectedValue={formData.vmware_type || 'normal'}
                                onSelect={(value) => {
                                    setFormData(prev => ({
                                        ...prev,
                                        vmware_type: value as 'normal' | 'vcenter',
                                        vcenter_host: ''
                                    }));
                                    setErrors({});
                                }}
                                width="w-full"
                                buttonHeight={8}
                            />
                        </div>
                        {formData.vmware_type === 'vcenter' && (
                            <div className="md:col-span-2">
                                <label className={labelBaseClass}>
                                    <Server className="w-3 h-3 mr-1.5 text-cyan-400" />
                                    vCenter Host
                                </label>
                                <input
                                    type="text"
                                    value={formData.vcenter_host || ''}
                                    onChange={(e) => setFormData(prev => ({ ...prev, vcenter_host: e.target.value }))}
                                    className={inputBaseClass}
                                    placeholder="vCenter Host"
                                />
                            </div>
                        )}
                        <div>
                            <label className={labelBaseClass}>
                                <Server className="w-3 h-3 mr-1.5 text-blue-400" />
                                Port
                            </label>
                            <input
                                type="number"
                                value={formData.port || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, port: parseInt(e.target.value) || undefined }))}
                                className={inputBaseClass}
                                placeholder="443"
                            />
                        </div>
                        <div>
                            <label className={labelBaseClass}>
                                <Key className="w-3 h-3 mr-1.5 text-green-400" />
                                User
                            </label>
                            <input
                                type="text"
                                value={formData.username || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                                className={inputBaseClass}
                                placeholder="Username"
                            />
                        </div>
                        <div className="md:col-span-2 text-xs">
                            <label className={labelBaseClass}>
                                <Key className="w-3 h-3 mr-1.5 text-purple-400" />
                                Private Key
                            </label>
                            <textarea
                                value={formData.private_key || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, private_key: e.target.value }))}
                                rows={3}
                                className={`${inputBaseClass} resize-none h-20 text-[9px]`}
                                placeholder="Paste key content..."
                            />
                        </div>
                    </div>
                );
            case 'camera':
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
                        <div>
                            <label className={labelBaseClass}>
                                <Key className="w-3 h-3 mr-1.5 text-green-400" />
                                User
                            </label>
                            <input
                                type="text"
                                value={formData.username || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                                className={inputBaseClass}
                                placeholder="User"
                            />
                        </div>
                        <div>
                            <label className={labelBaseClass}>
                                <Lock className="w-3 h-3 mr-1.5 text-yellow-400" />
                                Password
                            </label>
                            <input
                                type="password"
                                value={formData.password || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                                className={inputBaseClass}
                                placeholder="Password"
                            />
                        </div>
                        <div>
                            <label className={labelBaseClass}>
                                <Bot className="w-3 h-3 mr-1.5 text-blue-400" />
                                Make (Optional)
                            </label>
                            <input
                                type="text"
                                value={formData.make || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, make: e.target.value }))}
                                className={inputBaseClass}
                                placeholder="e.g. Hikvision, Dahua"
                            />
                        </div>
                        <div>
                            <label className={labelBaseClass}>
                                <Server className="w-3 h-3 mr-1.5 text-orange-400" />
                                Channel (Optional)
                            </label>
                            <input
                                type="number"
                                value={formData.channel || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, channel: e.target.value === '' ? '' : Number(e.target.value) }))}
                                className={inputBaseClass}
                                placeholder="e.g. 1"
                            />
                        </div>
                    </div >
                );
            case 'discovery':
                return (
                    <div className="space-y-4">
                        <div className="flex gap-2 items-end">
                            <div className="flex-1">
                                <label className={labelBaseClass}>
                                    <Server className="w-3 h-3 mr-1.5 text-blue-400" />
                                    Add CIDR (e.g., 192.168.1.0/24) <span className="text-red-500 ml-0.5">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="new-cidr-input"
                                    className={`${inputBaseClass} !bg-white dark:!bg-gray-800/40`}
                                    placeholder="192.168.1.0/24"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            const val = (e.target as HTMLInputElement).value.trim();
                                            if (val) {
                                                const parts = val.split('/');
                                                const ipv4Regex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
                                                const isValid = parts.length === 2 &&
                                                    ipv4Regex.test(parts[0]) &&
                                                    !isNaN(parseInt(parts[1])) &&
                                                    parseInt(parts[1]) >= 16 &&
                                                    parseInt(parts[1]) <= 32;

                                                if (!isValid) {
                                                    setErrors(prev => ({ ...prev, cidrs: 'Invalid CIDR format or prefix not in /16 to /32 range' }));
                                                    return;
                                                }

                                                setFormData(prev => ({
                                                    ...prev,
                                                    cidrs: [...(prev.cidrs || []), val]
                                                }));
                                                (e.target as HTMLInputElement).value = '';
                                                setErrors(prev => {
                                                    const { cidrs, ...rest } = prev;
                                                    return rest;
                                                });
                                            }
                                        }
                                    }}
                                />
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    const input = document.getElementById('new-cidr-input') as HTMLInputElement;
                                    const val = input.value.trim();
                                    if (val) {
                                        const parts = val.split('/');
                                        const ipv4Regex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
                                        const isValid = parts.length === 2 &&
                                            ipv4Regex.test(parts[0]) &&
                                            !isNaN(parseInt(parts[1])) &&
                                            parseInt(parts[1]) >= 16 &&
                                            parseInt(parts[1]) <= 32;

                                        if (!isValid) {
                                            setErrors(prev => ({ ...prev, cidrs: 'Invalid CIDR format or prefix not in /16 to /32 range' }));
                                            return;
                                        }

                                        setFormData(prev => ({
                                            ...prev,
                                            cidrs: [...(prev.cidrs || []), val]
                                        }));
                                        input.value = '';
                                        setErrors(prev => {
                                            const { cidrs, ...rest } = prev;
                                            return rest;
                                        });
                                    }
                                }}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold rounded-lg transition-colors"
                            >
                                Add
                            </button>
                        </div>
                        {errors.cidrs && (
                            <p className={errorClass}>
                                <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                                {errors.cidrs}
                            </p>
                        )}
                        <div className="flex flex-wrap gap-2 mt-2">
                            {formData.cidrs?.map((cidr, index) => (
                                <div key={index} className="bg-gray-100 dark:bg-gray-900 border border-gray-300 dark:border-gray-700/50 px-2 py-1 rounded flex items-center gap-2 group transition-all hover:border-orange-500/30">
                                    <span className="text-[11px] font-mono text-gray-800 dark:text-gray-300">{cidr}</span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setFormData(prev => ({
                                                ...prev,
                                                cidrs: prev.cidrs?.filter((_, i) => i !== index)
                                            }));
                                        }}
                                        className="text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 transition-colors"
                                    >
                                        <X className="w-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            case 'default':
                return (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h4 className="text-xs font-bold text-gray-900 dark:text-gray-200 uppercase tracking-wider flex items-center gap-2">
                                    <Key className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />
                                    Default Fallback Credentials
                                </h4>
                                <p className="text-[10px] text-gray-500 dark:text-gray-400">
                                    Add target-specific credentials (camera, switch/snmp, firewall, access point, nvr, vmware) to attempt during scans.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    setEditingDefaultId(null);
                                    setNewDefaultType('camera');
                                    setNewDefaultData({});
                                    setShowAddDefaultModal(true);
                                }}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold rounded-lg transition-colors"
                            >
                                <Plus className="w-3.5 h-3.5" />
                                Add Credential
                            </button>
                        </div>

                        {errors.defaults && (
                            <p className={errorClass}>
                                <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                                {errors.defaults}
                            </p>
                        )}

                        {/* Added List */}
                        {discoveryDefaults.length > 0 ? (
                            <div className="space-y-2">
                                {discoveryDefaults.map((defItem) => (
                                    <div
                                        key={defItem.id}
                                        className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-3 flex items-start justify-between gap-3 hover:border-gray-300 dark:hover:border-gray-700 transition-all"
                                    >
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-2">
                                                <span className="px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20">
                                                    {defItem.credential_type === 'snmp' ? 'SNMP / Switch' : defItem.credential_type.replace('_', ' ')}
                                                </span>
                                                {defItem.credential_data.snmp_version && (
                                                    <span className="text-[10px] text-gray-500 dark:text-gray-400 font-mono">
                                                        {defItem.credential_data.snmp_version}
                                                    </span>
                                                )}
                                                {defItem.credential_data.firewall_type && (
                                                    <span className="text-[10px] text-gray-500 dark:text-gray-400 font-mono">
                                                        ({defItem.credential_data.firewall_type})
                                                    </span>
                                                )}
                                            </div>

                                            <div className="text-[11px] text-gray-600 dark:text-gray-300 font-mono flex flex-wrap gap-x-4 gap-y-1">
                                                {defItem.credential_data.username && (
                                                    <span>User: <strong className="text-gray-900 dark:text-white">{defItem.credential_data.username}</strong></span>
                                                )}
                                                {defItem.credential_data.community && (
                                                    <span>Community: <strong className="text-gray-900 dark:text-white">{defItem.credential_data.community}</strong></span>
                                                )}
                                                {defItem.credential_data.snmp_username && (
                                                    <span>SNMP User: <strong className="text-gray-900 dark:text-white">{defItem.credential_data.snmp_username}</strong></span>
                                                )}
                                                {defItem.credential_data.make && (
                                                    <span>Make: <strong className="text-gray-900 dark:text-white">{defItem.credential_data.make}</strong></span>
                                                )}
                                                {defItem.credential_data.api_key && (
                                                    <span>API Key: <strong className="text-gray-900 dark:text-white">••••••••</strong></span>
                                                )}
                                                {defItem.credential_data.hostname && (
                                                    <span>Host: <strong className="text-gray-900 dark:text-white">{defItem.credential_data.hostname}</strong></span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-1">
                                            <button
                                                type="button"
                                                onClick={() => handleEditDefaultCredential(defItem)}
                                                className="text-gray-400 hover:text-orange-500 dark:hover:text-orange-400 p-1 transition-colors"
                                                title="Edit Credential"
                                            >
                                                <Edit className="w-4 h-4" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setDiscoveryDefaults(prev => prev.filter(d => d.id !== defItem.id));
                                                }}
                                                className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 p-1 transition-colors"
                                                title="Remove Credential"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="p-4 border border-dashed border-gray-300 dark:border-gray-800 rounded-lg text-center bg-gray-50 dark:bg-gray-900/30">
                                <p className="text-[11px] text-gray-500 dark:text-gray-400">No default fallback credentials added yet.</p>
                            </div>
                        )}

                        {/* Add / Edit Credential Form Box */}
                        {showAddDefaultModal && (
                            <div className="mt-3 bg-white dark:bg-gray-900 border border-orange-500/40 rounded-xl p-4 space-y-4 shadow-xl">
                                <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 pb-2">
                                    <h5 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
                                        {editingDefaultId ? <Edit className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" /> : <Plus className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />}
                                        {editingDefaultId ? 'Edit Default Credential' : 'Add Default Credential'}
                                    </h5>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setEditingDefaultId(null);
                                            setNewDefaultData({});
                                            setShowAddDefaultModal(false);
                                        }}
                                        className="text-gray-400 hover:text-gray-700 dark:hover:text-white p-1"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div className="md:col-span-2">
                                        <label className={labelBaseClass}>Credential Type</label>
                                        <CustomDropdown
                                            data={[
                                                { label: 'SNMP / Switch', value: 'snmp' },
                                                { label: 'Camera', value: 'camera' },
                                                { label: 'Firewall', value: 'firewall' },
                                                { label: 'Access Point', value: 'access_point' },
                                                { label: 'NVR', value: 'nvr' },
                                                { label: 'VMware', value: 'vmware' }
                                            ]}
                                            placeholder="Select Type"
                                            selectedValue={newDefaultType}
                                            onSelect={(val) => {
                                                setNewDefaultType(val as any);
                                                setNewDefaultData({});
                                            }}
                                            width="w-full"
                                            buttonHeight={8}
                                        />
                                    </div>

                                    {/* Render fields based on newDefaultType */}
                                    {renderNewDefaultFields()}
                                </div>

                                <div className="flex justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-800">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setEditingDefaultId(null);
                                            setNewDefaultData({});
                                            setShowAddDefaultModal(false);
                                        }}
                                        className="px-3 py-1 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white text-[10px] font-bold uppercase"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSaveDefaultCredential}
                                        className="px-4 py-1 bg-orange-600 hover:bg-orange-700 text-white rounded text-[10px] font-bold uppercase"
                                    >
                                        {editingDefaultId ? 'Update Credential' : 'Save Credential'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                );
            case 'ilo':
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
                        <div className="md:col-span-2">
                            <label className={labelBaseClass}>
                                <Server className="w-3 h-3 mr-1.5 text-blue-400" />
                                Host <span className="text-red-500 ml-0.5">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.host || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, host: e.target.value }))}
                                className={inputBaseClass}
                                placeholder="Enter Host IP or Hostname"
                            />
                            {errors.host && (
                                <p className={errorClass}>
                                    <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                                    {errors.host}
                                </p>
                            )}
                        </div>
                        <div>
                            <label className={labelBaseClass}>
                                <Key className="w-3 h-3 mr-1.5 text-green-400" />
                                User <span className="text-red-500 ml-0.5">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.username || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                                className={inputBaseClass}
                                placeholder="Username"
                            />
                            {errors.username && (
                                <p className={errorClass}>
                                    <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                                    {errors.username}
                                </p>
                            )}
                        </div>
                        <div>
                            <label className={labelBaseClass}>
                                <Lock className="w-3 h-3 mr-1.5 text-yellow-400" />
                                Password <span className="text-red-500 ml-0.5">*</span>
                            </label>
                            <input
                                type="password"
                                value={formData.password || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                                className={inputBaseClass}
                                placeholder="Password"
                            />
                            {errors.password && (
                                <p className={errorClass}>
                                    <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                                    {errors.password}
                                </p>
                            )}
                        </div>
                        <div className="md:col-span-2">
                            <label className={labelBaseClass}>
                                <Bot className="w-3 h-3 mr-1.5 text-orange-400" />
                                Port (Optional)
                            </label>
                            <input
                                type="number"
                                value={formData.port || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, port: parseInt(e.target.value) || undefined }))}
                                className={inputBaseClass}
                                placeholder="443"
                            />
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="p-4 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
            {/* Header */}
            <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-200 dark:border-gray-800">
                <div className="flex items-center">
                    <button
                        type="button"
                        onClick={onBack}
                        className="mr-2 text-gray-600 dark:text-gray-100 hover:text-gray-900 dark:hover:text-white transition-colors p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div className="flex items-center gap-2">
                        <div className="bg-orange-600/20 p-1.5 rounded">
                            <Settings
                                className="h-4 w-4 text-orange-500"
                                style={isSubmitting ? { animation: 'spin 3s linear infinite' } : {}}
                            />
                        </div>
                        <h2 className="text-base font-bold text-gray-900 dark:text-gray-100">
                            {isEdit ? 'Edit Configuration' : 'New Configuration'}
                        </h2>
                    </div>
                </div>
            </div>

            <div className="space-y-4">
                {/* Basic Info Group */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="flex items-center text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest mb-1.5">
                            <Key className="w-3 h-3 mr-1.5 text-orange-500 dark:text-orange-400" />
                            Credential Name <span className="text-red-500 ml-0.5">*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.credential_name}
                            onChange={(e) => {
                                setFormData(prev => ({ ...prev, credential_name: e.target.value }));
                                if (errors.credential_name) {
                                    setErrors(prev => {
                                        const { credential_name, ...rest } = prev;
                                        return rest;
                                    });
                                }
                            }}
                            className={`w-full bg-white dark:bg-gray-800/40 border ${errors.credential_name ? 'border-red-500/50' : 'border-gray-300 dark:border-gray-700/50'} rounded px-3 py-1.5 text-[11px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 
                                focus:outline-none focus:border-orange-500/50 transition-all duration-200`}
                            placeholder="e.g. Core Switch SNMP"
                        />
                        {errors.credential_name && (
                            <p className="mt-1 text-[9px] text-red-500 dark:text-red-400 font-bold uppercase tracking-tight flex items-center">
                                <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                                {errors.credential_name}
                            </p>
                        )}
                    </div>

                    <div>
                        <label className="flex items-center text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest mb-1.5">
                            <Shield className="w-3 h-3 mr-1.5 text-blue-500 dark:text-blue-400" />
                            Credential Type <span className="text-red-500 ml-0.5">*</span>
                        </label>
                        <CustomDropdown
                            data={credentialTypeOptions}
                            placeholder="Select Type"
                            selectedValue={formData.credential_type}
                            onSelect={(value) => {
                                setFormData(prev => ({
                                    ...prev,
                                    credential_type: value as any,
                                    snmp_type: value === 'snmp' ? 'v1' : (value === 'nvr' ? 'v2c' : undefined),
                                    firewall_type: value === 'firewall' ? 'cisco' : undefined,
                                    vmware_type: value === 'vmware' ? 'normal' : undefined,
                                    cidrs: value === 'discovery' ? prev.cidrs : [],
                                    host: value === 'ilo' ? prev.host : '',
                                    port: value === 'ilo' || value === 'vmware' ? prev.port : undefined,
                                    log_monitor_enable: false,
                                    syslog_port: 514
                                }));
                                if (value === 'discovery' || value === 'default') {
                                    setSelectedAsset(null);
                                    setSelectedAssets([]);
                                    setIpAddresses([]);
                                }
                                setErrors({});
                            }}
                            width="w-full"
                            buttonHeight={8}
                        />
                    </div>
                </div>

                {/* IP Addresses Section - Redesigned for Compactness */}
                <div className="">

                    {formData.credential_type !== 'discovery' && formData.credential_type !== 'default' && (
                        <div className="space-y-3 mb-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                        <Layers className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />
                                        Asset Type
                                    </label>
                                    <CustomDropdown
                                        data={assetTypeOptions}
                                        placeholder="All Asset Types"
                                        selectedValue={selectedAssetType}
                                        onSelect={(value) => {
                                            setSelectedAssetType(value);
                                            setSelectedAssets([]);
                                            setIpAddresses([]);
                                            setSelectedAsset(null);
                                        }}
                                        width="w-full"
                                        buttonHeight={11}
                                    />
                                </div>
                                <div>
                                    <MultiSelectApiDropdown
                                        label="Filter by Tags"
                                        apiEndpoint="/tags"
                                        displayProperty="name"
                                        searchFields={['name']}
                                        searchInputPlaceholder="Search tags..."
                                        placeholder="Select tags"
                                        additionalQueries={{ client_id: agent.client_id }}
                                        icon={Tag}
                                        className="w-full"
                                        value={selectedTags}
                                        onChange={(tags) => {
                                            setSelectedTags(tags);
                                            setSelectedAssets([]);
                                            setIpAddresses([]);
                                            setSelectedAsset(null);
                                        }}
                                    />
                                </div>
                            </div>

                            <div>
                                <MultiSelectApiDropdownWithAll
                                    label="Search Asset to add IP"
                                    apiEndpoint="/assets"
                                    displayProperty="name"
                                    secondaryDisplayProperty="ip_address"
                                    searchFields={['name', 'ip_address', 'hostname']}
                                    searchInputPlaceholder="Search assets..."
                                    placeholder="Select assets to add IP"
                                    icon={Server}
                                    className="w-full"
                                    value={selectedAssets}
                                    error={errors.asset_id}
                                    additionalQueries={assetQueries}
                                    onChange={(assets) => {
                                        setSelectedAssets(assets);
                                        const isSelectAll = assets.some(a => a.id === '*');
                                        if (isSelectAll) {
                                            setIpAddresses([]);
                                            setSelectedAsset({ id: '*', name: 'Select all' });
                                        } else {
                                            const ips = assets
                                                .map(a => a.ip_address)
                                                .filter((ip): ip is string => Boolean(ip));
                                            setIpAddresses(ips);
                                            if (assets.length > 0) {
                                                setSelectedAsset(assets[0]);
                                            } else {
                                                setSelectedAsset(null);
                                            }
                                        }
                                        setErrors(prev => {
                                            const { asset_id, ...rest } = prev;
                                            return rest;
                                        });
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {errors.ip_addresses && <p className="mb-2 text-[9px] text-red-500 dark:text-red-400 font-bold uppercase">{errors.ip_addresses}</p>}
                </div>

                {/* Dynamic Credential Fields - Compact Grid */}
                <div className="bg-gray-50 dark:bg-gray-800/10 border-l-2 border-orange-600/50 p-3 space-y-3">
                    {renderCredentialFields()}
                </div>
            </div>

            {/* Footer Actions */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-800">
                <div className="flex-1">
                    {validationErrorMsg && (
                        <div className="flex items-center gap-1.5 text-red-600 dark:text-red-400 text-xs font-semibold animate-fade-in bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-500/30 px-3 py-1.5 rounded-lg">
                            <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 shrink-0" />
                            <span>{validationErrorMsg}</span>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                    <button
                        type="button"
                        onClick={onBack}
                        disabled={isSubmitting}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-gray-600 hover:text-gray-900 dark:text-gray-100 dark:hover:text-white text-[10px] font-bold uppercase tracking-widest transition-all"
                    >
                        <X className="w-3.5 h-3.5" />
                        Discard
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="flex items-center gap-2 px-6 py-1.5 bg-orange-600 hover:bg-orange-700 text-white rounded text-[11px] font-bold shadow-lg shadow-orange-900/20 transition-all uppercase tracking-widest disabled:opacity-50"
                    >
                        {isSubmitting ? (
                            <>
                                <Loader className="w-3.5 h-3.5 animate-spin" />
                                Syncing...
                            </>
                        ) : (
                            <>
                                <Check className="w-3.5 h-3.5" />
                                {isEdit ? 'Update' : 'Save Config'}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CredentialForm;
